import React, { useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
} from "@mui/material";
import {
  AddCircleOutlineRounded,
  AddCircleOutline,
  Close,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { adminService } from "../services/adminService";
import {
  ROUTES,
  buttonGradient,
  buttonGradientHover,
  buttonShadow,
} from "../../../constants/consts";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    transition: "box-shadow 0.25s ease, background-color 0.25s ease",
    "&:hover": { backgroundColor: "rgba(4,87,129,0.02)" },
    "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(4,87,129,0.10)" },
  },
};

const EMPTY = { title: "", description: "" };

export default function AdminKnowledgeAdd() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({ open: false, msg: "" });
  const fileRef = useRef(null);

  const handleChange = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      setError("Konu ve içerik zorunludur.");
      return;
    }
    setSaving(true);
    setError("");

    try {
      let finalImage = "";
      if (file) {
        setUploading(true);
        const res = await adminService.uploadFile(
          file,
          "BilgiBankasi",
          "image",
        );
        setUploading(false);
        finalImage = res?.url || "";
      }

      await adminService.addKnowledge({
        title: form.title.trim(),
        description: form.description.trim(),
        image: finalImage,
      });

      setSnack({ open: true, msg: "Kayıt başarıyla eklendi." });
      setForm(EMPTY);
      setFile(null);
      setPreviewUrl("");
      setTimeout(() => navigate(ROUTES.ADMIN_KNOWLEDGE), 900);
    } catch (err) {
      console.error(err);
      setError("Kayıt eklenemedi.");
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        gap: 1.5,
      }}
    >
      {/* Başlık */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          animation: `${fadeInUp} 0.4s ease-out both`,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.14)",
            border: "1px solid rgba(255,255,255,0.20)",
          }}
        >
          <AddCircleOutlineRounded sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Box>
          <Typography
            variant="h6"
            fontWeight={800}
            color="#fff"
            sx={{ fontSize: 18 }}
          >
            Yeni Makale Ekle
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            Bilgi bankasına yeni bir makale ekleyin
          </Typography>
        </Box>
      </Box>

      {/* Form */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          overflowY: "auto",
          pt: 1,
          pb: 2,
        }}
      >
        <Paper
          elevation={0}
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: 3,
            backgroundColor: "rgba(255,255,255,0.98)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.20)",
            border: "1px solid rgba(255,255,255,0.30)",
            width: "100%",
            maxWidth: { xs: "100%", sm: 800, md: 900 },
            animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s backwards`,
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Resim */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Box
              sx={{
                position: "relative",
                width: 160,
                height: 160,
                borderRadius: 2,
                backgroundColor: "#F8FAFC",
                border: "2px dashed rgba(4,87,129,0.20)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {previewUrl ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="preview"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    color: "#94A3B8",
                    fontSize: 12,
                  }}
                >
                  <AddCircleOutline
                    sx={{ fontSize: 32, mb: 1, opacity: 0.5 }}
                  />
                  <div>Resim ekle</div>
                </Box>
              )}

              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleFile}
              />

              <IconButton
                size="small"
                onClick={() => fileRef.current?.click()}
                disabled={saving}
                sx={{
                  position: "absolute",
                  bottom: 6,
                  right: 6,
                  backgroundColor: "rgba(255,255,255,0.95)",
                  color: "#15803D",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  "&:hover": { backgroundColor: "#fff" },
                }}
              >
                <AddCircleOutline sx={{ fontSize: 20 }} />
              </IconButton>

              {previewUrl && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setFile(null);
                    setPreviewUrl("");
                  }}
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    backgroundColor: "rgba(255,255,255,0.95)",
                    color: "#B91C1C",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    "&:hover": { backgroundColor: "#fff" },
                  }}
                >
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Konu *"
              value={form.title}
              onChange={handleChange("title")}
              fullWidth
              size="small"
              sx={inputSx}
            />
            <TextField
              label="İçerik *"
              value={form.description}
              onChange={handleChange("description")}
              fullWidth
              multiline
              minRows={6}
              maxRows={16}
              size="small"
              sx={inputSx}
            />
          </Box>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              gap: 1.5,
              justifyContent: "flex-end",
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate(ROUTES.ADMIN_KNOWLEDGE)}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                py: 1.2,
                fontWeight: 600,
              }}
            >
              Vazgeç
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || uploading}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 700,
                px: 4,
                py: 1.2,
                background: buttonGradient,
                boxShadow: buttonShadow,
                "&:hover": { background: buttonGradientHover },
                "&:disabled": { background: "#c7c7c7" },
              }}
            >
              {saving || uploading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Kaydet"
              )}
            </Button>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack({ open: false, msg: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
